'use client';

import React from 'react';
import { Card, Select } from '@ui';
import PaginationNavigation from './PaginationNavigation';
import { PAGE_SIZE_OPTIONS, type PaginationItem } from './PaginationData';

type PaginationProps = {
  id?: string;
  items: PaginationItem[];
  // Controlled props (optional). If omitted, Pagination manages its own state.
  page?: number;
  pageSize?: number;
  totalPages?: number;
  rows?: PaginationItem[];
  pagerNumbers?: Array<number | '…'>;
  goto?: (p: number) => void;
  onPageSizeChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  // Uncontrolled defaults
  defaultPageSize?: number;
  onPageChange?: (p: number) => void;
  // number of pages to skip for PageUp/PageDown
  step?: number;
  renderRow?: (item: PaginationItem, index: number) => React.ReactNode;
};

export default function Pagination({
  id,
  items,
  page,
  pageSize,
  totalPages,
  rows,
  pagerNumbers,
  goto,
  onPageSizeChange,
  defaultPageSize = 5,
  onPageChange,
  step = 5,
  renderRow,
}: PaginationProps) {
  // Determine controlled vs uncontrolled
  const isControlled = typeof page === 'number';
  const isPageSizeControlled = typeof pageSize === 'number';

  const [internalPage, setInternalPage] = React.useState<number>(page ?? 1);
  const [internalPageSize, setInternalPageSize] = React.useState<number>(
    pageSize ?? defaultPageSize
  );

  // Keep internal state in sync if parent switches to controlled mode
  React.useEffect(() => {
    if (isControlled && typeof page === 'number') setInternalPage(page);
  }, [isControlled, page]);

  React.useEffect(() => {
    if (isPageSizeControlled && typeof pageSize === 'number') setInternalPageSize(pageSize);
  }, [isPageSizeControlled, pageSize]);

  const currentPage = isControlled ? (page as number) : internalPage;
  const currentPageSize = isPageSizeControlled ? (pageSize as number) : internalPageSize;

  // compute derived data when not provided by parent
  const computedTotalPages = Math.max(1, Math.ceil(items.length / currentPageSize));
  // prefer explicit prop if parent provides totalPages
  const usedTotalPages = typeof totalPages === 'number' ? totalPages : computedTotalPages;
  const displayRows =
    rows ??
    items.slice(
      (currentPage - 1) * currentPageSize,
      (currentPage - 1) * currentPageSize + currentPageSize
    );

  // always compute the pager numbers via useMemo (hooks must not be conditional)
  const memoPagerNumbers = React.useMemo(() => {
    const t = Math.max(1, Math.ceil(items.length / currentPageSize));
    const visible: Array<number | '…'> = [];
    const windowSize = 5;
    const s = Math.max(1, currentPage - 2);
    const realStart = Math.max(1, Math.min(s, t - windowSize + 1));

    if (realStart > 1) visible.push(1, '…');
    for (let i = realStart; i <= Math.min(t, realStart + windowSize - 1); i++) visible.push(i);
    const last = visible[visible.length - 1];
    if (typeof last === 'number' && last < t) visible.push('…', t);

    return visible;
  }, [items.length, currentPage, currentPageSize]);
  const displayPagerNumbers = pagerNumbers ?? memoPagerNumbers;

  // choose goto handler: forwarded or internal
  function internalGoto(p: number) {
    const next = Math.min(Math.max(1, p), usedTotalPages);
    if (!isControlled) setInternalPage(next);
    if (typeof onPageChange === 'function') onPageChange(next);
  }

  const gotoFn = goto ?? internalGoto;

  function internalOnPageSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const nextSize = parseInt(e.target.value, 10) || defaultPageSize;
    if (!isPageSizeControlled) {
      setInternalPageSize(nextSize);
      // reset to first page
      setInternalPage(1);
    }
    if (typeof onPageSizeChange === 'function') onPageSizeChange(e);
  }

  const pageSizeChangeHandler = internalOnPageSizeChange;

  // keyboard handler: allow arrow left/right to navigate pages when focus
  // is on the pagination container (or non-interactive child). We skip
  // handling when an interactive element (button/input/select/etc.) is
  // focused to avoid interfering with native controls.
  function handleKeyDown(e: React.KeyboardEvent<HTMLElement>) {
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) return;
    const stepSize = step; // page increment for PageUp/PageDown
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      gotoFn(currentPage + 1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      gotoFn(currentPage - 1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      gotoFn(1);
    } else if (e.key === 'End') {
      e.preventDefault();
      gotoFn(usedTotalPages);
    } else if (e.key === 'PageDown') {
      e.preventDefault();
      gotoFn(Math.min(usedTotalPages, currentPage + stepSize));
    } else if (e.key === 'PageUp') {
      e.preventDefault();
      gotoFn(Math.max(1, currentPage - stepSize));
    }
  }

  return (
    <Card className='pagination-card' id={id ?? 'pagiCard'}>
      <div className='row' style={{ justifyContent: 'space-between' }}>
        <div className='row mb-16'>
          <label className='row'>
            <span className='hint'>Items per page</span>
            <Select
              className='select'
              id='pagiPageSize'
              value={String(currentPageSize)}
              onChange={pageSizeChangeHandler}
              options={PAGE_SIZE_OPTIONS.map((size) => ({
                value: String(size),
                label: String(size),
              }))}
            />
          </label>
        </div>
        <div className='row mb-16'>
          <small className='hint' id='pagiInfo' data-testid='pagiInfo'>
            {items.length} items • Page {currentPage} of {usedTotalPages}
          </small>
        </div>
      </div>

      <ul className='pagi-list' id='pagiList' aria-live='polite'>
        {displayRows.map((r, idx) => (
          <li className='card' key={r.id}>
            {typeof renderRow === 'function' ? (
              renderRow(r, idx)
            ) : (
              <>
                <b>{r.name}</b>
                <div className='hint'>{r.note}</div>
              </>
            )}
          </li>
        ))}
      </ul>

      <PaginationNavigation
        currentPage={currentPage}
        totalPages={usedTotalPages}
        pagerNumbers={displayPagerNumbers}
        goto={gotoFn}
        onKeyDown={handleKeyDown}
      />
    </Card>
  );
}
